/** Reporting SLA events — catalog v8 / 1.9.0 additions. */
import {
  asEventRecord,
  defineEvent,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export type ReportingFamily = 'sales' | 'customers' | 'activities' | 'money' | 'staff-hours';

export interface ReportingSlaBreachedPayload extends Record<string, unknown> {
  reportId: string;
  family: ReportingFamily;
  elapsedMs: number;
  slaMs: number;
  paramsRedacted: true;
}

const REPORTING_SLA_BREACHED_TYPE = 'reporting:sla-breached';
const REPORT_FAMILIES: readonly ReportingFamily[] = [
  'sales',
  'customers',
  'activities',
  'money',
  'staff-hours',
];

const reportingSlaBreachedSchema: EventSchema<ReportingSlaBreachedPayload> = {
  parse(data) {
    const record = asEventRecord(data, REPORTING_SLA_BREACHED_TYPE);
    const reportId = requireString(record.reportId, 'reportId', REPORTING_SLA_BREACHED_TYPE);
    const family = requireString(record.family, 'family', REPORTING_SLA_BREACHED_TYPE);
    if (!REPORT_FAMILIES.includes(family as ReportingFamily)) {
      throw new Error(`${REPORTING_SLA_BREACHED_TYPE}: field "family" must be a valid report family`);
    }

    const elapsedMs = record.elapsedMs;
    if (typeof elapsedMs !== 'number' || !Number.isSafeInteger(elapsedMs) || elapsedMs < 0) {
      throw new Error(`${REPORTING_SLA_BREACHED_TYPE}: field "elapsedMs" must be a non-negative safe integer`);
    }

    const slaMs = record.slaMs;
    if (typeof slaMs !== 'number' || !Number.isSafeInteger(slaMs) || slaMs < 1) {
      throw new Error(`${REPORTING_SLA_BREACHED_TYPE}: field "slaMs" must be a positive safe integer`);
    }

    if (record.paramsRedacted !== true) {
      throw new Error(`${REPORTING_SLA_BREACHED_TYPE}: field "paramsRedacted" must be true`);
    }

    // Rebuild the payload from an allowlist; report parameter values never cross this boundary.
    return { reportId, family: family as ReportingFamily, elapsedMs, slaMs, paramsRedacted: true };
  },
};

export const REPORTING_SLA_BREACHED_EVENT = defineEvent<ReportingSlaBreachedPayload>({
  type: REPORTING_SLA_BREACHED_TYPE,
  version: 1,
  schema: reportingSlaBreachedSchema,
  auditable: true,
  description: 'A registered report exceeded its service-level objective.',
  since: '1.9.0',
});

export const REPORTING_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [REPORTING_SLA_BREACHED_EVENT.type]: REPORTING_SLA_BREACHED_EVENT,
};
